import React, { useState, useEffect } from "react";
import { toast } from "sonner";

const PAUSE_COUNT_KEY = "stratos.undo.pause-count";

function readPauseCount() {
  try {
    const raw = sessionStorage.getItem(PAUSE_COUNT_KEY);
    const n = parseInt(raw, 10);
    return Number.isFinite(n) && n >= 0 ? n : 0;
  } catch {
    return 0;
  }
}

function writePauseCount(n) {
  try {
    if (n > 0) sessionStorage.setItem(PAUSE_COUNT_KEY, String(n));
    else sessionStorage.removeItem(PAUSE_COUNT_KEY);
  } catch {}
}

/**
 * Iter 70 — Undo Countdown Chip.
 * Iter 71 — Halt on hover.
 * Iter 72 — Halt on keyboard focus.
 * Iter 73 — Amber flash cue on pause.
 * Iter 74 — Pause history counter chip.
 * Iter 75 — Double-click resets the counter.
 * Iter 76 — Extracted to its own component + sessionStorage persistence so
 *           the pause count survives Sonner list re-renders.
 */
export default function UndoActionLabel({ seconds = 5 }) {
  const [remaining, setRemaining] = useState(seconds);
  const [paused, setPaused] = useState(false);
  const [justPaused, setJustPaused] = useState(false);
  // Iter 76 — Pause History Persist. Hydrated from sessionStorage so a
  // Sonner list re-render (e.g., when a sibling toast dismisses)
  // doesn't drop the count back to 0.
  const [pauseCount, setPauseCount] = useState(readPauseCount);
  const rootRef = React.useRef(null);

  useEffect(() => {
    if (!paused) { setJustPaused(false); return undefined; }
    setJustPaused(true);
    setPauseCount((n) => {
      const next = n + 1;
      writePauseCount(next);
      return next;
    });
    const t = setTimeout(() => setJustPaused(false), 400);
    return () => clearTimeout(t);
  }, [paused]);

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return undefined;
    const toastEl = el.closest("[data-sonner-toast]") || el.closest("li[data-sonner-toast]");
    const btn = el.closest("button");
    if (!toastEl && !btn) return undefined;
    const enter = () => setPaused(true);
    const leave = () => setPaused(false);
    if (toastEl) {
      toastEl.addEventListener("mouseenter", enter);
      toastEl.addEventListener("mouseleave", leave);
    }
    if (btn) {
      btn.addEventListener("focus", enter);
      btn.addEventListener("blur", leave);
    }
    return () => {
      if (toastEl) {
        toastEl.removeEventListener("mouseenter", enter);
        toastEl.removeEventListener("mouseleave", leave);
      }
      if (btn) {
        btn.removeEventListener("focus", enter);
        btn.removeEventListener("blur", leave);
      }
    };
  }, []);

  useEffect(() => {
    if (remaining <= 0) return undefined;
    if (paused) return undefined;
    const t = setTimeout(() => setRemaining((n) => Math.max(0, n - 1)), 1000);
    return () => clearTimeout(t);
  }, [remaining, paused]);

  return (
    <span
      ref={rootRef}
      data-testid="undo-countdown-chip"
      data-remaining={remaining}
      data-paused={paused ? "true" : "false"}
      data-just-paused={justPaused ? "true" : "false"}
      className="inline-flex items-center gap-1 tabular-nums"
    >
      Undo <span
        role="timer"
        aria-live="polite"
        aria-label={(() => {
          // Iter 78 — Localised aria-label. Use Intl.RelativeTimeFormat
          // so the "expires in N seconds" phrasing matches the
          // browser's locale (mirrors sonner's own locale-aware
          // toast copy) — improves screen-reader consistency for
          // non-en users.
          let rel = `in ${remaining} seconds`;
          try {
            const rtf = new Intl.RelativeTimeFormat(undefined, {
              numeric: "always",
              style: "long",
            });
            rel = rtf.format(remaining, "second");
          } catch {
            /* Intl unavailable → keep en-US fallback */
          }
          return `Undo expires ${rel}${paused ? ", paused" : ""}`;
        })()}
        className={
          "text-[10px] transition-colors duration-300 " +
          (justPaused ? "text-amber-300" : "text-zinc-500")
        }
      >
        {remaining}s
      </span>
      {pauseCount > 0 && (
        <span
          data-testid="undo-pause-history-chip"
          data-count={pauseCount}
          role="button"
          tabIndex={0}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          onDoubleClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setPauseCount(0);
            writePauseCount(0);
            toast("Pause counter reset", { duration: 2000 });
          }}
          className="mono text-[9px] tabular-nums text-zinc-600 border border-zinc-700 rounded-sm px-1 py-[1px] ml-1 cursor-pointer hover:border-zinc-500 hover:text-zinc-400 select-none"
          title={`Held ${pauseCount} time${pauseCount === 1 ? "" : "s"} this session · double-click to reset`}
        >
          paused ×{pauseCount}
        </span>
      )}
    </span>
  );
}
